import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type KeyboardEvent,
} from "react";
import Icon from "@mdi/react";
import { mdiDeleteOutline, mdiPlus } from "@mdi/js";
import { Button, Input, ListBox, Select, TextField } from "@heroui/react";
import type { HelpLineItem, HelpService, HelpServiceKind } from "../types";
import { lineItemAmount } from "../types";
import {
  HELP_PAYMENT_LABEL,
  HELP_PAYMENT_OPTIONS,
  formatHelpMoney,
} from "../constants";
import type { HelpPaymentStatus } from "../types";

function PaymentStatusSelect({
  value,
  onChange,
  label,
}: {
  value: HelpPaymentStatus;
  onChange: (status: HelpPaymentStatus) => void;
  label: string;
}) {
  return (
    <Select
      selectedKey={value}
      aria-label={label}
      onSelectionChange={(key) => {
        if (!key) return;
        onChange(String(key) as HelpPaymentStatus);
      }}
    >
      <Select.Trigger>
        <Select.Value />
        <Select.Indicator />
      </Select.Trigger>
      <Select.Popover>
        <ListBox>
          {HELP_PAYMENT_OPTIONS.map((status) => (
            <ListBox.Item
              key={status}
              id={status}
              textValue={HELP_PAYMENT_LABEL[status]}
            >
              {HELP_PAYMENT_LABEL[status]}
            </ListBox.Item>
          ))}
        </ListBox>
      </Select.Popover>
    </Select>
  );
}

interface LineRowPayload {
  title: string;
  price: number;
  serviceUuid: string | null;
}

interface HelpReceiptLineRowProps {
  enabledCatalog: HelpService[];
  initialTitle: string;
  initialPrice: number;
  initialServiceUuid: string | null;
  placeholder?: string;
  deleteLabel: string;
  onSave: (payload: LineRowPayload) => void;
  onDelete: () => void;
  autoFocus?: boolean;
}

function HelpReceiptLineRow({
  enabledCatalog,
  initialTitle,
  initialPrice,
  initialServiceUuid,
  placeholder = "Услуга из каталога или свой пункт",
  deleteLabel,
  onSave,
  onDelete,
  autoFocus = false,
}: HelpReceiptLineRowProps) {
  const [title, setTitle] = useState(initialTitle);
  const [price, setPrice] = useState(initialPrice ? String(initialPrice) : "");
  const [serviceUuid, setServiceUuid] = useState(initialServiceUuid);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setTitle(initialTitle);
    setPrice(initialPrice ? String(initialPrice) : "");
    setServiceUuid(initialServiceUuid);
  }, [initialTitle, initialPrice, initialServiceUuid]);

  const filteredSuggestions =
    title.trim().length >= 1
      ? enabledCatalog.filter((service) =>
          service.title.toLowerCase().includes(title.trim().toLowerCase()),
        )
      : enabledCatalog;

  const resetDraft = () => {
    setTitle(initialTitle);
    setPrice(initialPrice ? String(initialPrice) : "");
    setServiceUuid(initialServiceUuid);
    setShowSuggestions(false);
  };

  const selectCatalogSuggestion = (service: HelpService) => {
    setTitle(service.title);
    setPrice(String(service.unitPrice));
    setServiceUuid(service.uuid);
    setShowSuggestions(false);
    onSave({
      title: service.title,
      price: service.unitPrice,
      serviceUuid: service.uuid,
    });
  };

  const commit = () => {
    const trimmedTitle = title.trim();
    const numPrice = Number(price);
    if (!trimmedTitle || Number.isNaN(numPrice) || numPrice < 0) {
      resetDraft();
      return;
    }
    onSave({ title: trimmedTitle, price: numPrice, serviceUuid });
  };

  const handlePriceKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();
      commit();
    }
  };

  useEffect(() => {
    const handler = (event: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <li className="help-receipt__row help-receipt__row--draft">
      <div className="help-receipt__inline-add">
        <div className="help-receipt-autocomplete" ref={suggestionsRef}>
          <TextField fullWidth>
            <Input
              placeholder={placeholder}
              value={title}
              autoFocus={autoFocus}
              onChange={(event) => {
                setTitle(event.target.value);
                setServiceUuid(null);
                setShowSuggestions(true);
              }}
              onFocus={() => setShowSuggestions(true)}
              onBlur={() => {
                window.setTimeout(() => {
                  setShowSuggestions(false);
                  commit();
                }, 120);
              }}
            />
          </TextField>
          {showSuggestions && filteredSuggestions.length > 0 ? (
            <div className="help-receipt-suggestions">
              {filteredSuggestions.map((service) => (
                <button
                  key={service.uuid}
                  type="button"
                  className="help-receipt-suggestion"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => selectCatalogSuggestion(service)}
                >
                  <span className="help-receipt-suggestion__title">
                    {service.title}
                  </span>
                  <span className="help-receipt-suggestion__price">
                    {formatHelpMoney(service.unitPrice)}
                  </span>
                </button>
              ))}
            </div>
          ) : null}
        </div>
        <TextField className="help-receipt__inline-price">
          <Input
            placeholder="Цена, ₽"
            value={price}
            onChange={(event) => setPrice(event.target.value)}
            onBlur={commit}
            onKeyDown={handlePriceKeyDown}
          />
        </TextField>
      </div>
      <div className="help-receipt__row-actions">
        <Button
          isIconOnly
          size="sm"
          variant="ghost"
          aria-label={deleteLabel}
          onMouseDown={(event) => event.preventDefault()}
          onPress={onDelete}
        >
          <Icon path={mdiDeleteOutline} size={0.7} />
        </Button>
      </div>
    </li>
  );
}

interface HelpReceiptEditorProps {
  items: HelpLineItem[];
  catalog: HelpService[];
  orderKind: HelpServiceKind;
  prepaidAmount: number;
  prepaidStatus: HelpPaymentStatus;
  extraPaymentStatus: HelpPaymentStatus;
  onItemsChange: (items: HelpLineItem[]) => void;
  onPrepaidChange: (amount: number) => void;
  onPrepaidStatusChange: (status: HelpPaymentStatus) => void;
  onExtraStatusChange: (status: HelpPaymentStatus) => void;
  embedded?: boolean;
  onSave?: () => void;
  onCancel?: () => void;
}

const HelpReceiptEditor: FC<HelpReceiptEditorProps> = ({
  items,
  catalog,
  orderKind,
  prepaidAmount,
  prepaidStatus,
  extraPaymentStatus,
  onItemsChange,
  onPrepaidChange,
  onPrepaidStatusChange,
  onExtraStatusChange,
  embedded = false,
  onSave,
  onCancel,
}) => {
  const [isAdding, setIsAdding] = useState(false);

  const enabledCatalog = catalog.filter(
    (service) =>
      service.enabled &&
      (service.kind === orderKind || service.kind === "addon"),
  );
  const total = useMemo(
    () => items.reduce((sum, item) => sum + lineItemAmount(item), 0),
    [items],
  );
  const extra = Math.max(0, total - prepaidAmount);

  const buildLineItem = (
    payload: LineRowPayload,
    id: string,
    quantity = 1,
  ): HelpLineItem | null => {
    const { title, price, serviceUuid } = payload;

    if (serviceUuid) {
      const service = catalog.find((item) => item.uuid === serviceUuid);
      if (service) {
        return {
          id,
          source: "catalog",
          serviceUuid: service.uuid,
          code: service.code,
          title: service.title,
          quantity,
          unitPrice: price,
          pricingMode: service.pricingMode,
        };
      }
    }

    const matchedService = enabledCatalog.find(
      (service) => service.title.toLowerCase() === title.toLowerCase(),
    );
    if (matchedService) {
      return {
        id,
        source: "catalog",
        serviceUuid: matchedService.uuid,
        code: matchedService.code,
        title: matchedService.title,
        quantity,
        unitPrice: price,
        pricingMode: matchedService.pricingMode,
      };
    }

    return {
      id,
      source: "custom",
      serviceUuid: null,
      code: "",
      title,
      quantity,
      unitPrice: price,
      pricingMode: "fixed",
    };
  };

  const updateItemFromPayload = (id: string, payload: LineRowPayload) => {
    const current = items.find((item) => item.id === id);
    if (!current) return;

    const next = buildLineItem(payload, id, current.quantity);
    if (!next) return;

    onItemsChange(items.map((item) => (item.id === id ? next : item)));
  };

  const addFromPayload = (payload: LineRowPayload) => {
    const next = buildLineItem(payload, `li-${Date.now()}`);
    if (!next) return;
    onItemsChange([...items, next]);
    setIsAdding(false);
  };

  const removeItem = (id: string) => {
    onItemsChange(items.filter((item) => item.id !== id));
  };

  return (
    <div className="help-receipt">
      <div className="help-receipt-card">
        <span className="help-receipt-card__label">Позиции чека</span>
        {items.length === 0 && !isAdding ? (
          <p className="help-view-empty">Пока нет позиций</p>
        ) : (
          <ul className="help-receipt__list">
            {items.map((item) => (
              <HelpReceiptLineRow
                key={item.id}
                enabledCatalog={enabledCatalog}
                initialTitle={item.title}
                initialPrice={item.unitPrice}
                initialServiceUuid={item.serviceUuid}
                deleteLabel={`Удалить ${item.title}`}
                onSave={(payload) => updateItemFromPayload(item.id, payload)}
                onDelete={() => removeItem(item.id)}
              />
            ))}

            {isAdding ? (
              <HelpReceiptLineRow
                key="__draft__"
                enabledCatalog={enabledCatalog}
                initialTitle=""
                initialPrice={0}
                initialServiceUuid={null}
                deleteLabel="Удалить черновик позиции"
                autoFocus
                onSave={addFromPayload}
                onDelete={() => setIsAdding(false)}
              />
            ) : null}
          </ul>
        )}

        {!isAdding ? (
          <Button size="sm" variant="tertiary" onPress={() => setIsAdding(true)}>
            <Icon path={mdiPlus} size={0.7} />
            Добавить
          </Button>
        ) : null}

        <div className="help-receipt__total">
          <span>Итого</span>
          <strong>{formatHelpMoney(total)}</strong>
        </div>
      </div>

      <div className="help-receipt-card">
        <span className="help-receipt-card__label">Оплата</span>
        <div className="help-pay-edit">
          <div className="help-pay-edit__row">
            <label className="help-form-field">
              Предоплата, ₽
              <TextField fullWidth>
                <Input
                  value={String(prepaidAmount)}
                  onChange={(e) =>
                    onPrepaidChange(Math.max(0, Number(e.target.value) || 0))
                  }
                />
              </TextField>
            </label>
            <label className="help-form-field help-form-field--status">
              Статус
              <PaymentStatusSelect
                value={prepaidStatus}
                onChange={onPrepaidStatusChange}
                label="Статус предоплаты"
              />
            </label>
          </div>
          <div className="help-pay-edit__row">
            <label className="help-form-field">
              Доплата
              <span className="help-pay-edit__readonly">{formatHelpMoney(extra)}</span>
            </label>
            <label className="help-form-field help-form-field--status">
              Статус
              <PaymentStatusSelect
                value={extraPaymentStatus}
                onChange={onExtraStatusChange}
                label="Статус доплаты"
              />
            </label>
          </div>
        </div>
      </div>

      {!embedded ? (
        <div className="help-edit-actions">
          <Button size="sm" variant="tertiary" onPress={onCancel}>
            Отмена
          </Button>
          <Button size="sm" variant="primary" onPress={onSave}>
            Сохранить чек
          </Button>
        </div>
      ) : null}
    </div>
  );
};

export default HelpReceiptEditor;
